import { IonContent, IonModal } from '@ionic/react';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type Dispatch,
  type PropsWithChildren,
  type Ref,
  type SetStateAction,
} from 'react';
import { createPortal } from 'react-dom';

import styles from './TagModal.module.css';

import { Button } from '../Button';
import { Divider } from '../Divider/Divider';
import { Icon } from '../Icon';
import { Input } from '../Input';
import { ModalFooter } from '../ModalFooter/ModalFooter';
import { ModalHeader } from '../ModalHeader/ModalHeader';
import { Tag } from '../Tag/Tag';

interface ModalProps extends ComponentProps<typeof IonModal> {
  ref?: Ref<HTMLIonModalElement>;
  setShowModal: Dispatch<SetStateAction<boolean>>;
}

const Tags: string[] = ['Walk (Easy)', 'Walk (Hard)', 'Walk (Intermediate)'];

// TODO: Retrieve a list of tags from the DB
// TODO: Select tag

export const TagModal = ({ setShowModal, ref, ...props }: PropsWithChildren<ModalProps>) => {
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [tags, setTags] = useState('');

  const filteredTags = useMemo(() => {
    return Tags.filter((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [tags]);
  const handleTagSelect = (tag: string) => {
    setTags(tag);
    setSearchTerm('');
  };
  useEffect(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      setTags('');
      setSearchTerm('');
    };
  }, []);

  const cleanUp = () => {
    setSearchTerm('');
    setTags('');
    setShowModal(false);
  };

  return (
    <>
      <IonModal className={styles.modal} {...props} onDidDismiss={cleanUp} ref={ref}>
        <ModalHeader>{'Add Tags'}</ModalHeader>
        <IonContent className={styles.content}>
          <div className={styles.inner} ref={inputRef}>
            <Input
              leadingIcon='search'
              onIonInput={(event) => {
                setSearchTerm(event.detail.value ?? '');
              }}
              placeholder='Search for, or create a tag...'
              trailingIcon={searchTerm ? 'cross-circle' : undefined}
              value={tags ? tags : searchTerm}
              variant='primary'>
              Tags
            </Input>
          </div>
        </IonContent>
        <ModalFooter>
          {' '}
          <>
            <Button onClick={cleanUp} variant='secondary'>
              Cancel
            </Button>
            <div>
              <Button
                onClick={() => {
                  setSearchTerm('');
                  setTags('');
                }}
                variant='secondary'>
                Clear tag
              </Button>

              <Button onClick={() => setShowModal(true)} variant='primary'>
                Add tag
              </Button>
            </div>
          </>
        </ModalFooter>
      </IonModal>

      {searchTerm &&
        createPortal(
          <div
            className={styles.dropdown}
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              zIndex: 9999,
            }}>
            {filteredTags.length > 0 &&
              filteredTags.map((tag) => (
                <div key={tag} style={{ width: '100%' }}>
                  <div className={styles.dropdownItem} onClick={() => handleTagSelect(tag)}>
                    <Tag variant='measurement'>{tag}</Tag>
                  </div>
                  <Divider />
                </div>
              ))}
            <div className={styles.dropdownItem} onClick={() => handleTagSelect(searchTerm)}>
              <Icon name='plus' size={14} />
              <p className={styles.dropdownText}>
                Create tag: <strong>&apos;{searchTerm}&apos;</strong>
              </p>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
